import React from 'react';

import { Cell, Header, Row, Table } from '@tanstack/react-table';

import Checkbox from 'components/Checkbox/Checkbox';

import { cn } from '../lib/utils';
import { SELECT_ALL_ROWS_ID } from './DataTable.consts';

export { SELECT_ALL_ROWS_ID };

/**
 * Generates a test ID for a table row based on the row identifier.
 * @template T - The type of data in the row.
 * @param row - The row instance from react-table.
 * @param rowIdIdentifier - The property name to use as the identifier.
 * @returns The test ID string, or undefined if no identifier is provided.
 */
export function getRowTestId<T extends object>(
  row: Row<T>,
  rowIdIdentifier: string
): string | undefined {
  if (!rowIdIdentifier) {
    return undefined;
  }
  return (row.original as Record<string, string>)[rowIdIdentifier];
}

/**
 * Generates a test ID for the drag handle of a row.
 * @template T - The type of data in the row.
 * @param row - The row instance from react-table.
 * @param rowIdIdentifier - The property name to use as the identifier.
 * @returns The drag handle test ID string.
 */
export function getDragHandleTestId<T extends object>(
  row: Row<T>,
  rowIdIdentifier: string
): string {
  if (rowIdIdentifier) {
    const rowId = (row.original as Record<string, string>)[rowIdIdentifier];
    return `product-row-drag-handle-${rowId}`;
  }
  return `row-drag-handle-${row.id}`;
}

/**
 * Gets the width of a cell when column resizing is enabled.
 * @template T - The type of data in the row.
 * @param cell - The cell instance from react-table.
 * @param enableColumnResizing - Whether column resizing is enabled.
 * @returns The cell width in pixels, or undefined if resizing is disabled.
 */
export function getCellWidth<T extends object>(
  cell: Cell<T, unknown>,
  enableColumnResizing: boolean
): number | undefined {
  if (enableColumnResizing) {
    return cell.column.getSize();
  }
  return undefined;
}

/**
 * Gets the width of a header when column resizing is enabled.
 * @template T - The type of data in the row.
 * @param header - The header instance from react-table.
 * @param enableColumnResizing - Whether column resizing is enabled.
 * @returns The header width in pixels, or undefined if resizing is disabled.
 */
export function getHeaderWidth<T extends object>(
  header: Header<T, unknown>,
  enableColumnResizing: boolean
): number | undefined {
  if (enableColumnResizing) {
    return header.getSize();
  }
  return undefined;
}

/**
 * Gets the appropriate FontAwesome icon class for the current sort direction.
 * @template T - The type of data in the row.
 * @param header - The header instance from react-table.
 * @returns The FontAwesome icon class string.
 */
export function getSortIcon<T extends object>(
  header: Header<T, unknown>
): string {
  const sortDirection = header.column.getIsSorted();
  if (sortDirection === 'asc') {
    return 'fa-solid fa-arrow-up';
  }
  if (sortDirection === 'desc') {
    return 'fa-solid fa-arrow-down';
  }
  return 'fa-solid fa-arrow-up';
}

/**
 * Gets the CSS class names for the sort icon based on sort state.
 * Icon is hidden by default and shown on hover when not sorted.
 * @template T - The type of data in the row.
 * @param header - The header instance from react-table.
 * @returns The CSS class string for the sort icon.
 */
export function getSortIconClassName<T extends object>(
  header: Header<T, unknown>
): string {
  const isSorted = header.column.getIsSorted();
  const baseClassName = 'text-xs text-base-mutedForeground transition-opacity';

  if (isSorted) {
    return baseClassName;
  }
  return cn(baseClassName, 'opacity-0 group-hover:opacity-100');
}

/**
 * Determines the table layout CSS property based on column resizing.
 * @param enableColumnResizing - Whether column resizing is enabled.
 * @returns 'auto' when resizing is enabled, 'fixed' otherwise.
 */
export function getTableLayout(
  enableColumnResizing: boolean
): 'auto' | 'fixed' {
  if (enableColumnResizing) {
    return 'auto';
  }
  return 'fixed';
}

/**
 * Generates CSS class names for the table container.
 * @param bordered - Whether the table has a border.
 * @param stickyHeader - Whether the header is sticky.
 * @returns The combined CSS class string.
 */
export function getContainerClassName(
  bordered: boolean,
  stickyHeader: boolean
): string {
  const classNames = ['border-base-sidebarBorder rounded-lg'];

  if (bordered) {
    classNames.push('border');
  } else {
    classNames.push('border-0');
  }

  if (!stickyHeader) {
    classNames.push('overflow-hidden');
  }

  return cn(...classNames);
}

/**
 * Generates CSS class names for the table element.
 * Applies fixed layout with constrained last column when resizing is disabled.
 * @param enableColumnResizing - Whether column resizing is enabled.
 * @param className - Additional custom class name.
 * @returns The combined CSS class string.
 */
export function getTableClassName(
  enableColumnResizing: boolean,
  className?: string
): string {
  const classNames = ['w-full'];

  if (enableColumnResizing) {
    classNames.push('table-auto');
  } else {
    classNames.push(
      'table-fixed',
      '[&_td:last-child]:w-[60px]',
      '[&_td:last-child]:max-w-[60px]',
      '[&_th:last-child]:w-[60px]',
      '[&_th:last-child]:max-w-[60px]'
    );
  }

  if (className) {
    classNames.push(className);
  }

  return cn(...classNames);
}

/**
 * Creates a column definition for the row selection checkbox column.
 * Includes a header checkbox for "select all" and cell checkboxes for each row.
 * @param isDisabled - Whether all checkboxes should be disabled.
 * @returns A column definition object compatible with @tanstack/react-table.
 */
export function geTableSelectionColumn(isDisabled?: boolean) {
  return {
    id: SELECT_ALL_ROWS_ID,
    header: ({ table }: { table: Table<any> }) => (
      <Checkbox
        checked={
          table.getIsAllRowsSelected()
            ? true
            : table.getIsSomeRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(checked) => table.toggleAllRowsSelected(!!checked)}
        isDisabled={isDisabled}
      />
    ),
    cell: ({ row }: { row: Row<any> }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(checked) => row.toggleSelected(!!checked)}
        isDisabled={isDisabled}
      />
    ),
    size: 50
  };
}
